// TODO: add indexes to necessary tables

import { createId } from "@paralleldrive/cuid2";
import { type ExtractTablesWithRelations, relations, sql } from "drizzle-orm";
import type { NodePgQueryResultHKT } from "drizzle-orm/node-postgres";
import {
  type PgTransaction,
  boolean,
  index,
  integer,
  json,
  pgEnum,
  pgTable,
  text,
  timestamp,
  unique,
  uuid,
} from "drizzle-orm/pg-core";
import type { schema } from ".";
import { AVATAR_COLORS, DEFAULT_AVATAR_COLOR } from "../schemas/avatar";
import {
  FORM_TYPES,
  type InvestigationParticipant,
  type Participant,
  type Photo,
  type Timing,
  type Video,
  type Witness,
} from "../schemas/common";
import type { FieldGeneric } from "../schemas/form";
import { DEFAULT_LANGUAGE, LANGUAGES } from "../schemas/language";
import { ROLES } from "../schemas/role";

export type DbTransaction = PgTransaction<
  NodePgQueryResultHKT,
  typeof schema,
  ExtractTablesWithRelations<typeof schema>
>;

export const colorEnum = pgEnum("color", AVATAR_COLORS);
export const roleEnum = pgEnum("role", ROLES);
export const languageEnum = pgEnum("language", LANGUAGES);
export const formStatusEnum = pgEnum("form_status", [
  "in_progress",
  "completed",
]);

export const user = pgTable("user", {
  // WARNING:
  // Because we are creating some users manually we are using same function as better auth library
  id: text("id")
    .primaryKey()
    .$defaultFn(() => createId()),
  name: text("name").notNull(),
  email: text("email").notNull().unique(),
  emailVerified: boolean("email_verified").notNull(),
  image: text("image"),
  createdAt: timestamp("created_at").notNull(),
  updatedAt: timestamp("updated_at").notNull(),
  role: text("role").default("user"),
  color: colorEnum().default(DEFAULT_AVATAR_COLOR).notNull(),
  language: languageEnum().default(DEFAULT_LANGUAGE).notNull(),

  // Banning user
  banned: boolean("banned").default(false),
  banReason: text("ban_reason"),
  banExpires: timestamp("ban_expires"),

  // Needed for better auth plugin
  // Stripe customer ID
  stripeCustomerId: text("stripe_customer_id"),

  // Resend ID
  resendId: text("resend_id"),
});

export const session = pgTable("session", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => createId()),
  expiresAt: timestamp("expires_at").notNull(),
  token: text("token").notNull().unique(),
  createdAt: timestamp("created_at").notNull(),
  updatedAt: timestamp("updated_at").notNull(),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  userId: text("user_id")
    .notNull()
    .references(() => user.id, { onDelete: "cascade" }),
  impersonatedBy: text("impersonated_by"),
});

export const account = pgTable("account", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => createId()),
  accountId: text("account_id").notNull(),
  providerId: text("provider_id").notNull(),
  userId: text("user_id")
    .notNull()
    .references(() => user.id, { onDelete: "cascade" }),
  accessToken: text("access_token"),
  refreshToken: text("refresh_token"),
  idToken: text("id_token"),
  accessTokenExpiresAt: timestamp("access_token_expires_at"),
  refreshTokenExpiresAt: timestamp("refresh_token_expires_at"),
  scope: text("scope"),
  password: text("password"),
  createdAt: timestamp("created_at").notNull(),
  updatedAt: timestamp("updated_at").notNull(),
});

export const verification = pgTable("verification", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => createId()),
  identifier: text("identifier").notNull(),
  value: text("value").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});

export const company = pgTable("company", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => createId()),
  name: text("name").notNull(),
  companyCode: text("company_code"), // TODO: add unique constraint and not null
  color: colorEnum().default(DEFAULT_AVATAR_COLOR).notNull(),
  userLimit: integer("user_limit").notNull().default(5),
  usersUsage: integer("users_usage").notNull().default(0),
  mailboxAdviceEmail: text("mailbox_advice_email"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at")
    .notNull()
    .$onUpdate(() => new Date()),
});

export const subscription = pgTable("subscription", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => createId()),
  plan: text("plan").notNull(),
  referenceId: text("reference_id")
    .references(() => company.id, {
      onDelete: "cascade",
    })
    .notNull(),
  stripeCustomerId: text("stripe_customer_id"),
  stripeSubscriptionId: text("stripe_subscription_id"),
  status: text("status").notNull(),
  periodStart: timestamp("period_start"),
  periodEnd: timestamp("period_end"),
  cancelAtPeriodEnd: boolean("cancel_at_period_end"),
  seats: integer("seats"),
  trialStart: timestamp("trial_start"),
  trialEnd: timestamp("trial_end"),
});

export type Subscription = typeof subscription.$inferSelect;

export type Company = typeof company.$inferSelect;
export type Location = typeof location.$inferSelect;
export type Zone = typeof zone.$inferSelect;
export type JobPosition = typeof jobPosition.$inferSelect;

export const department = pgTable("department", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => createId()),
  departmentCode: text("department_code").notNull().unique(),
  name: text("name").notNull(),
  companyId: text("company_id")
    .notNull()
    .references(() => company.id, { onDelete: "cascade" }),
  color: colorEnum().default(DEFAULT_AVATAR_COLOR).notNull(),
  monthlyObjectives: json()
    .$type<{
      drill: number | null;
      hseq_visit: number | null;
      investigation: number | null;
      observation: number | null;
      management: number | null;
      actions: number | null;
    }>()
    .default({
      drill: null,
      hseq_visit: null,
      investigation: null,
      observation: null,
      management: null,
      actions: null,
    }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at")
    .notNull()
    .$onUpdate(() => new Date()),
});

// Location table - company-scoped
export const location = pgTable("location", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => createId()),
  name: text("name").notNull(),
  description: text("description"), // Optional description for the location
  companyId: text("company_id")
    .notNull()
    .references(() => company.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at")
    .notNull()
    .$onUpdate(() => new Date()),
});

// Zone table - location-scoped
export const zone = pgTable("zone", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => createId()),
  name: text("name").notNull(),
  description: text("description"), // Optional description for the zone
  locationId: text("location_id")
    .notNull()
    .references(() => location.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at")
    .notNull()
    .$onUpdate(() => new Date()),
});

// Job Position table - company-scoped
export const jobPosition = pgTable("job_position", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => createId()),
  name: text("name").notNull(),
  description: text("description"), // Optional description for the job position
  companyId: text("company_id")
    .notNull()
    .references(() => company.id, { onDelete: "cascade" }),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at")
    .notNull()
    .$onUpdate(() => new Date()),
});

export const companyUser = pgTable(
  "company_user",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    companyId: text("company_id")
      .notNull()
      .references(() => company.id, { onDelete: "cascade" }),
    userId: text("user_id")
      .references(() => user.id, { onDelete: "cascade" })
      .notNull(),
    departmentId: text("department_id").references(() => department.id, {
      onDelete: "restrict",
    }), // This should stay nullable to allow orphaned users (regular or not).
    role: roleEnum().notNull(),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at")
      .notNull()
      .$onUpdate(() => new Date()),
  },
  (t) => [
    unique("user_company_department").on(t.userId, t.companyId, t.departmentId),
  ],
);

export const invite = pgTable("invite", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => createId()),
  companyId: text("company_id")
    .notNull()
    .references(() => company.id, { onDelete: "cascade" }),
  role: roleEnum().notNull(),
  invitationCode: uuid("invitation_id").notNull().unique(),
  invitationExpiresAt: timestamp("invitation_expires_at").notNull(),
  email: text("email").notNull(), // For showing pending invites
});

export const inviteDepartment = pgTable(
  "invite_department",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    inviteId: text("invite_id")
      .notNull()
      .references(() => invite.id, { onDelete: "cascade" }),
    departmentId: text("department_id")
      .notNull()
      .references(() => department.id, { onDelete: "cascade" }),
  },
  (t) => [unique("inv_dep").on(t.inviteId, t.departmentId)],
);

export const formTypeEnum = pgEnum("form_type", FORM_TYPES);

export const drillForm = pgTable("drill_form", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => createId()),
  viewId: text("view_id").notNull().unique(),
  type: formTypeEnum().default("drill").notNull(),
  departmentId: text("department_id")
    .notNull()
    .references(() => department.id, { onDelete: "cascade" }),
  createdById: text("created_by_id")
    .notNull()
    .references(() => user.id, { onDelete: "restrict" }), // User who created/filled the form

  status: formStatusEnum("status").default("in_progress").notNull(),
  // Location and zone references
  date: timestamp("date").notNull(),
  locationId: text("location_id")
    .notNull()
    .references(() => location.id, { onDelete: "restrict" }),
  zoneId: text("zone_id")
    .notNull()
    .references(() => zone.id, { onDelete: "restrict" }),
  scenario: text("scenario").notNull(),
  objective: text("objective"),
  development: text("development"),
  timings: json().$type<Timing[]>(),
  // TODO: might need to be in a separate table
  participants: json().$type<Participant[]>(),
  // TODO: might need to be in a separate table
  photos: json().$type<Photo[]>(),
  // TODO: might need to be in a separate table
  videos: json().$type<Video[]>(),
  insituCorrections: text("insitu_corrections"),
  conclusion: text("conclusion"),
  conductorSignature: text("conductor_signature"),
  initiatorSignature: text("initiator_signature"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at")
    .notNull()
    .$onUpdate(() => new Date()),
});

export const managementForm = pgTable("management_form", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => createId()),
  viewId: text("view_id").notNull().unique(),
  type: formTypeEnum().default("management").notNull(),
  departmentId: text("department_id")
    .notNull()
    .references(() => department.id, { onDelete: "cascade" }),
  createdById: text("created_by_id")
    .notNull()
    .references(() => user.id, { onDelete: "restrict" }), // User who created/filled the form

  status: formStatusEnum("status").default("in_progress").notNull(),
  // Location and zone references
  date: timestamp("date").notNull(),
  locationId: text("location_id")
    .notNull()
    .references(() => location.id, { onDelete: "restrict" }),
  zoneId: text("zone_id")
    .notNull()
    .references(() => zone.id, { onDelete: "restrict" }),
  changeInitiator: text("change_initiator"),
  changeName: text("change_name"),
  changeDescription: text("change_description"),
  changeJustification: text("change_justification"),
  changeType: text("change_type"),
  initiatorSignature: text("initiator_signature"),

  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at")
    .notNull()
    .$onUpdate(() => new Date()),
});

export const visitForm = pgTable("visit_form", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => createId()),
  viewId: text("view_id").notNull().unique(),
  type: formTypeEnum().default("hseq_visit").notNull(),
  departmentId: text("department_id")
    .notNull()
    .references(() => department.id, { onDelete: "cascade" }),
  createdById: text("created_by_id")
    .notNull()
    .references(() => user.id, { onDelete: "restrict" }), // User who created/filled the form

  status: formStatusEnum("status").default("in_progress").notNull(),
  // Location and zone references
  date: timestamp("date").notNull(),
  locationId: text("location_id")
    .notNull()
    .references(() => location.id, { onDelete: "restrict" }),
  zoneId: text("zone_id")
    .notNull()
    .references(() => zone.id, { onDelete: "restrict" }),
  evaluatedProcedures: text("evaluated_procedures").array().default([]),
  observer: text("observer"),
  // TODO: might need to be in a separate table
  participants: json().$type<Participant[]>(),
  // TODO: might need to be in a separate table
  photos: json().$type<Photo[]>(),
  // TODO: might need to be in a separate table
  videos: json().$type<Video[]>(),
  strongPoints: text("strong_points"),
  insituCorrections: text("insitu_corrections"),
  observations: text("observations"),
  observerSignature: text("observer_signature"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at")
    .notNull()
    .$onUpdate(() => new Date()),
});

export const investigationForm = pgTable("investigation_form", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => createId()),
  viewId: text("view_id").notNull().unique(),
  type: formTypeEnum().default("investigation").notNull(),
  departmentId: text("department_id")
    .notNull()
    .references(() => department.id, { onDelete: "cascade" }),
  createdById: text("created_by_id")
    .notNull()
    .references(() => user.id, { onDelete: "restrict" }), // User who created/filled the form

  status: formStatusEnum("status").default("in_progress").notNull(),
  description: text("description"),
  // Location and zone references
  date: timestamp("date").notNull(),
  locationId: text("location_id")
    .notNull()
    .references(() => location.id, { onDelete: "restrict" }),
  zoneId: text("zone_id")
    .notNull()
    .references(() => zone.id, { onDelete: "restrict" }),
  participants: json().$type<InvestigationParticipant[]>(),
  witnesses: json().$type<Witness[]>(),
  timings: json().$type<Timing[]>(),
  eventType: text("event_type"),
  investigatedBy: text("investigated_by"),
  detectedBy: text("detected_by"),
  assistedBy: text("assisted_by"),
  taskBeingPerformed: text("task_being_performed"),
  equipmentOrSubstanceInvolved: text("equipment_or_substance_involved"),
  eventDescription: text("event_description"),

  photos: json().$type<Photo[]>(),
  videos: json().$type<Video[]>(),
  insituCorrections: text("insitu_corrections"),
  additionalComments: text("additional_comments"),
  conclusion: text("conclusion"),
  investigationSignature: text("investigation_signature"),
  conductorSignature: text("conductor_signature"),
  preventionServiceSignature: text("prevention_service_signature"),

  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at")
    .notNull()
    .$onUpdate(() => new Date()),
  closedAt: timestamp("closed_at"),
});

export const observationForm = pgTable("observation_form", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => createId()),
  viewId: text("view_id").notNull().unique(),
  type: formTypeEnum().default("observation").notNull(),
  departmentId: text("department_id")
    .notNull()
    .references(() => department.id, { onDelete: "cascade" }),
  createdById: text("created_by_id")
    .notNull()
    .references(() => user.id, { onDelete: "restrict" }), // User who created/filled the form

  status: formStatusEnum("status").default("in_progress").notNull(),
  // Basic info
  date: timestamp("date").notNull(),
  // Location and zone references
  locationId: text("location_id")
    .notNull()
    .references(() => location.id, { onDelete: "restrict" }),
  zoneId: text("zone_id")
    .notNull()
    .references(() => zone.id, { onDelete: "restrict" }),

  workPosition: text("work_position"),
  tasks: text("tasks"),

  // Optional fields
  equipmentUsed: text("equipment_used"),
  observationSequence: text("observation_sequence"),
  positiveActs: text("positive_acts"),
  deviations: text("deviations"),
  inSituCorrections: text("in_situ_corrections"),
  observationNotes: text("observation_notes"),

  // JSON fields for complex data
  observationParticipants: json().$type<Participant[]>(),
  observedParticipants: json().$type<Participant[]>(),
  photos: json().$type<Photo[]>(),
  videos: json().$type<Video[]>(),
  initiatorSignature: text("initiator_signature"),

  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at")
    .notNull()
    .$onUpdate(() => new Date()),
});

export const mailboxForm = pgTable("mailbox_form", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => createId()),
  viewId: text("view_id").notNull().unique(),
  type: formTypeEnum().default("mailbox").notNull(),
  companyId: text("company_id")
    .notNull()
    .references(() => company.id, { onDelete: "cascade" }),
  createdById: text("created_by_id")
    .notNull()
    .references(() => user.id, { onDelete: "restrict" }), // User who created/filled the form

  status: formStatusEnum("status").default("in_progress").notNull(),
  // Location and zone references
  date: timestamp("date").notNull(),
  locationId: text("location_id")
    .notNull()
    .references(() => location.id, { onDelete: "restrict" }),
  zoneId: text("zone_id")
    .notNull()
    .references(() => zone.id, { onDelete: "restrict" }),
  announcementImportance: text("announcement_importance"),
  communicationType: text("communication_type"),
  description: text("description"),
  immediateActions: text("immediate_actions"),
  possibleCauses: text("possible_causes"),
  announcementAssessment: text("announcement_assessment"),
  personalInjury: text("personal_injury"),
  propertyDamage: text("property_damage"),
  environmentDamage: text("environment_damage"),
  environmentalDamage: text("environmental_damage"),
  imageDamage: text("image_damage"),

  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at")
    .notNull()
    .$onUpdate(() => new Date()),
});

// Company-scoped counter for mailbox viewIds
export const mailboxSequenceCounter = pgTable(
  "mailbox_sequence_counter",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => createId()),
    companyId: text("company_id")
      .notNull()
      .references(() => company.id, { onDelete: "cascade" }),
    value: integer("value").notNull().default(0),
  },
  (t) => [unique("company_mailbox_counter_unique").on(t.companyId)],
);

export const formField = pgTable(
  "form_field",
  {
    id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
    visitFormId: text("visit_form_id").references(() => visitForm.id, {
      onDelete: "cascade",
    }),
    investigationFormId: text("investigation_form_id").references(
      () => investigationForm.id,
      {
        onDelete: "cascade",
      },
    ),
    observationFormId: text("observation_form_id").references(
      () => observationForm.id,
      {
        onDelete: "cascade",
      },
    ),
    managementFormId: text("management_form_id").references(
      () => managementForm.id,
      {
        onDelete: "cascade",
      },
    ),

    fieldOrder: integer("field_order").notNull().default(0),

    field: json().$type<FieldGeneric>().notNull(),

    corrections: json().$type<{ id: string; value: string }[]>().default([]),
  },
  (table) => [
    index("idx_visit_form_order").on(table.visitFormId, table.fieldOrder),
    index("idx_investigation_form_order").on(
      table.investigationFormId,
      table.fieldOrder,
    ),
    index("idx_observation_form_order").on(
      table.observationFormId,
      table.fieldOrder,
    ),
    index("idx_management_form_order").on(
      table.managementFormId,
      table.fieldOrder,
    ),
  ],
);

export const actionStatusEnum = pgEnum("action_status", [
  "in_progress",
  "completed",
  "evaluated",
]);

export const actionPriorityEnum = pgEnum("action_priority", [
  "0", // max priority
  "1",
  "2",
  "3",
  "4", // min priority
]);

// Counter table for generating monotonic viewIds per department and kind
export const sequenceCounter = pgTable(
  "sequence_counter",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => createId()),
    departmentId: text("department_id")
      .notNull()
      .references(() => department.id, { onDelete: "cascade" }),
    kind: text("kind").notNull(), // e.g., 'action' or `form:drill`
    value: integer("value").notNull().default(0),
  },
  (t) => [unique("department_kind_unique").on(t.departmentId, t.kind)],
);

export const action = pgTable("action", {
  id: text("id")
    .primaryKey()
    .$defaultFn(() => createId()),
  viewId: text("view_id").notNull().unique(),
  departmentId: text("department_id")
    .notNull()
    .references(() => department.id, { onDelete: "cascade" }),
  assignedUserId: text("assigned_user_id").references(() => user.id, {
    onDelete: "set null",
  }), // Action can be unassigned or user can be deleted

  initiatedBy: text("initiated_by")
    .references(() => user.id, {
      onDelete: "restrict", // Restrict is used to prevent the deletion of the user who initiated the action
    })
    .notNull(),

  // In case of coming from a visit form (Optional)
  visitFormId: text("visit_form_id").references(() => visitForm.id, {
    onDelete: "cascade",
  }),

  // In case of coming from a drill form (Optional)
  drillFormId: text("drill_form_id").references(() => drillForm.id, {
    onDelete: "cascade",
  }),

  // In case of coming from a management form (Optional)
  managementFormId: text("management_form_id").references(
    () => managementForm.id,
    {
      onDelete: "cascade",
    },
  ),

  // In case of coming from a investigation form (Optional)
  investigationFormId: text("investigation_form_id").references(
    () => investigationForm.id,
    {
      onDelete: "cascade",
    },
  ),

  // In case of coming from an observation form (Optional)
  observationFormId: text("observation_form_id").references(
    () => observationForm.id,
    {
      onDelete: "cascade",
    },
  ),

  // In case of coming from a mailbox form (Optional)
  mailboxFormId: text("mailbox_form_id").references(() => mailboxForm.id, {
    onDelete: "cascade",
  }),

  // There are actions coming from a form + specific field
  formFieldId: uuid("form_field_id").references(() => formField.id, {
    onDelete: "cascade",
  }),

  title: text("title").notNull(),
  status: actionStatusEnum().default("in_progress").notNull(),
  priority: actionPriorityEnum().default("4").notNull(), // 0 = max priority, 4 = min priority
  description: text("description"),
  actionType: text("action_type"),
  origin: text("origin"),
  cause: text("cause"),
  evaluationCriteria: text("evaluation_criteria"),
  evaluationResult: text("evaluation_result"),
  evaluationCost: integer("evaluation_cost"),
  closingDate: timestamp("closing_date"),
  dueDate: timestamp("due_date"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at")
    .notNull()
    .$onUpdate(() => new Date()),
});

////////////////////////////////////////////////////////////////////
// RELATIONS DRIZZLE API
// DEFINE THE ONES WHICH ARE GOING TO BE USED IN QUERIES MORE OFTEN
////////////////////////////////////////////////////////////////////
export const userRelations = relations(user, ({ many }) => ({
  sessions: many(session),
  accounts: many(account),
  companyUsers: many(companyUser),
  assignedActions: many(action, { relationName: "assignedUser" }),
  initiatedActions: many(action, { relationName: "initiatedByUser" }),
}));

export const companyRelations = relations(company, ({ many }) => ({
  departments: many(department),
  companyUsers: many(companyUser),
  invites: many(invite),
  locations: many(location),
  jobPositions: many(jobPosition),
  subscriptions: many(subscription),
}));

export const subscriptionRelations = relations(subscription, ({ one }) => ({
  company: one(company, {
    fields: [subscription.referenceId],
    references: [company.id],
  }),
}));

export const departmentRelations = relations(department, ({ one, many }) => ({
  company: one(company, {
    fields: [department.companyId],
    references: [company.id],
  }),
  companyUsers: many(companyUser),
  inviteDepartments: many(inviteDepartment),
  actions: many(action), // Department can have many actions
  drillForms: many(drillForm), // Add drill forms relation
  visitForms: many(visitForm), // Add visit forms relation
  investigationForms: many(investigationForm), // Add investigation forms relation
  observationForms: many(observationForm), // Add observation forms relation
  managementForms: many(managementForm), // Add management forms relation
}));

export const locationRelations = relations(location, ({ one, many }) => ({
  company: one(company, {
    fields: [location.companyId],
    references: [company.id],
  }),
  zones: many(zone),
  drillForms: many(drillForm),
  visitForms: many(visitForm),
  investigationForms: many(investigationForm),
  observationForms: many(observationForm),
  managementForms: many(managementForm),
}));

export const zoneRelations = relations(zone, ({ one, many }) => ({
  location: one(location, {
    fields: [zone.locationId],
    references: [location.id],
  }),
  drillForms: many(drillForm),
  visitForms: many(visitForm),
  investigationForms: many(investigationForm),
  observationForms: many(observationForm),
  managementForms: many(managementForm),
}));

export const jobPositionRelations = relations(jobPosition, ({ one, many }) => ({
  company: one(company, {
    fields: [jobPosition.companyId],
    references: [company.id],
  }),
  drillForms: many(drillForm),
  visitForms: many(visitForm),
  investigationForms: many(investigationForm),
  observationForms: many(observationForm),
  managementForms: many(managementForm),
}));

export const companyUserRelations = relations(companyUser, ({ one }) => ({
  company: one(company, {
    fields: [companyUser.companyId],
    references: [company.id],
  }),
  department: one(department, {
    fields: [companyUser.departmentId],
    references: [department.id],
  }),
  user: one(user, {
    fields: [companyUser.userId],
    references: [user.id],
  }),
}));

export const inviteRelations = relations(invite, ({ one, many }) => ({
  company: one(company, {
    fields: [invite.companyId],
    references: [company.id],
  }),
  inviteDepartments: many(inviteDepartment),
}));

export const inviteDepartmentRelations = relations(
  inviteDepartment,
  ({ one }) => ({
    invite: one(invite, {
      fields: [inviteDepartment.inviteId],
      references: [invite.id],
    }),
    department: one(department, {
      fields: [inviteDepartment.departmentId],
      references: [department.id],
    }),
  }),
);

export const actionRelations = relations(action, ({ one }) => ({
  department: one(department, {
    fields: [action.departmentId],
    references: [department.id],
  }),
  assignedUser: one(user, {
    fields: [action.assignedUserId],
    references: [user.id],
    relationName: "assignedUser",
  }),
  initiatedByUser: one(user, {
    fields: [action.initiatedBy],
    references: [user.id],
    relationName: "initiatedByUser",
  }),
  visitForm: one(visitForm, {
    fields: [action.visitFormId],
    references: [visitForm.id],
  }),
  formField: one(formField, {
    fields: [action.formFieldId],
    references: [formField.id],
  }),
  drillForm: one(drillForm, {
    fields: [action.drillFormId],
    references: [drillForm.id],
  }),
  investigationForm: one(investigationForm, {
    fields: [action.investigationFormId],
    references: [investigationForm.id],
  }),
  observationForm: one(observationForm, {
    fields: [action.observationFormId],
    references: [observationForm.id],
  }),
  managementForm: one(managementForm, {
    fields: [action.managementFormId],
    references: [managementForm.id],
  }),
  mailboxForm: one(mailboxForm, {
    fields: [action.mailboxFormId],
    references: [mailboxForm.id],
  }),
}));

export const drillFormRelations = relations(drillForm, ({ one, many }) => ({
  department: one(department, {
    fields: [drillForm.departmentId],
    references: [department.id],
  }),
  createdByUser: one(user, {
    fields: [drillForm.createdById],
    references: [user.id],
  }),
  location: one(location, {
    fields: [drillForm.locationId],
    references: [location.id],
  }),
  zone: one(zone, {
    fields: [drillForm.zoneId],
    references: [zone.id],
  }),
  actions: many(action),
}));

export const visitFormRelations = relations(visitForm, ({ one, many }) => ({
  department: one(department, {
    fields: [visitForm.departmentId],
    references: [department.id],
  }),
  createdByUser: one(user, {
    fields: [visitForm.createdById],
    references: [user.id],
  }),
  location: one(location, {
    fields: [visitForm.locationId],
    references: [location.id],
  }),
  zone: one(zone, {
    fields: [visitForm.zoneId],
    references: [zone.id],
  }),
  fields: many(formField),
  actions: many(action),
}));

export const managementFormRelations = relations(
  managementForm,
  ({ one, many }) => ({
    department: one(department, {
      fields: [managementForm.departmentId],
      references: [department.id],
    }),
    createdByUser: one(user, {
      fields: [managementForm.createdById],
      references: [user.id],
    }),
    location: one(location, {
      fields: [managementForm.locationId],
      references: [location.id],
    }),
    zone: one(zone, {
      fields: [managementForm.zoneId],
      references: [zone.id],
    }),
    fields: many(formField),
    actions: many(action),
  }),
);

export const formFieldRelations = relations(formField, ({ one, many }) => ({
  visitForm: one(visitForm, {
    fields: [formField.visitFormId],
    references: [visitForm.id],
  }),
  investigationForm: one(investigationForm, {
    fields: [formField.investigationFormId],
    references: [investigationForm.id],
  }),
  observationForm: one(observationForm, {
    fields: [formField.observationFormId],
    references: [observationForm.id],
  }),
  managementForm: one(managementForm, {
    fields: [formField.managementFormId],
    references: [managementForm.id],
  }),
  actions: many(action),
}));

export const investigationFormRelations = relations(
  investigationForm,
  ({ one, many }) => ({
    department: one(department, {
      fields: [investigationForm.departmentId],
      references: [department.id],
    }),
    createdByUser: one(user, {
      fields: [investigationForm.createdById],
      references: [user.id],
    }),
    location: one(location, {
      fields: [investigationForm.locationId],
      references: [location.id],
    }),
    zone: one(zone, {
      fields: [investigationForm.zoneId],
      references: [zone.id],
    }),
    fields: many(formField),
    actions: many(action),
  }),
);

export const observationFormRelations = relations(
  observationForm,
  ({ one, many }) => ({
    department: one(department, {
      fields: [observationForm.departmentId],
      references: [department.id],
    }),
    createdByUser: one(user, {
      fields: [observationForm.createdById],
      references: [user.id],
    }),
    location: one(location, {
      fields: [observationForm.locationId],
      references: [location.id],
    }),
    zone: one(zone, {
      fields: [observationForm.zoneId],
      references: [zone.id],
    }),
    fields: many(formField),
    actions: many(action),
  }),
);

export const mailboxFormRelations = relations(mailboxForm, ({ one, many }) => ({
  company: one(company, {
    fields: [mailboxForm.companyId],
    references: [company.id],
  }),
  createdByUser: one(user, {
    fields: [mailboxForm.createdById],
    references: [user.id],
  }),
  location: one(location, {
    fields: [mailboxForm.locationId],
    references: [location.id],
  }),
  zone: one(zone, {
    fields: [mailboxForm.zoneId],
    references: [zone.id],
  }),
  actions: many(action),
}));

export const mailboxSequenceCounterRelations = relations(
  mailboxSequenceCounter,
  ({ one }) => ({
    company: one(company, {
      fields: [mailboxSequenceCounter.companyId],
      references: [company.id],
    }),
  }),
);

export const sessionRelations = relations(session, ({ one }) => ({
  user: one(user, {
    fields: [session.userId],
    references: [user.id],
  }),
}));

export const accountRelations = relations(account, ({ one }) => ({
  user: one(user, {
    fields: [account.userId],
    references: [user.id],
  }),
}));
